import { createSlice } from '@reduxjs/toolkit';
import { PeriodLicense, TaxState } from '../../../types/tax';
import { EndpointData } from 'src/types/store';

// Set the initial state values of the state object that this slice will manage
const INITIAL_STATE: TaxState = {
  taxPaymentPeriods: {
    data: [],
    loading: false,
  },

  isCalculateTaxInProgress: false,
  isCalculateTaxSuccess: false,
  calculatedTaxData: { details: [] }, // raw data returned from  /calculateMultiPropertyTot
  calculatedTaxErrorMsg: '',

  preSelectedPeriod: undefined,

  taxFlowData: {
    selectedLicenses: [],
    reportingMethod: undefined,
  },
  licensesPerPeriod: {},
};

// Define the slice and generate actions, reducers with createSlice
export const taxSlice = createSlice({
  name: 'tax',
  initialState: INITIAL_STATE,
  reducers: {
    loadLicensesForPeriodStarted: (state, action) => {
      state.licensesPerPeriod[action.payload] = {
        data: [],
        loading: true,
        error: undefined,
      };
    },
    loadLicensesForPeriodFailure: (state, action) => {
      state.licensesPerPeriod[action.payload] = {
        data: [],
        loading: false,
        error: action.payload,
      };
    },
    addLicensesForPeriod: (state, action) => {
      const { licenses, period } = action.payload;
      state.licensesPerPeriod[period.startDate] = {
        data: licenses,
        loading: false,
        error: undefined,
      };
    },
    taxPaymentPeriodsStarted: (state) => {
      state.taxPaymentPeriods = { data: [], loading: true, error: undefined };
    },
    taxPaymentPeriodSucceeded: (state, action) => {
      const { paymentPeriods, licensesPerPeriod } = action.payload;
      state.taxPaymentPeriods = {
        data: paymentPeriods,
        loading: false,
        error: undefined,
      };
      const transformedLicensesPerPeriod = (
        Object.entries(licensesPerPeriod) as [string, PeriodLicense[]][]
      ).reduce(
        (prev, [key, data]) => {
          return {
            ...prev,
            [key]: {
              data,
              loading: false,
              error: undefined,
            },
          };
        },
        {} as Record<string, EndpointData<PeriodLicense>>
      );
      state.licensesPerPeriod = transformedLicensesPerPeriod;
    },
    taxPaymentPeriodFailure: (state, action) => {
      state.taxPaymentPeriods = {
        data: [],
        loading: false,
        error: action.payload,
      };
    },
    calculateTaxStarted: (state) => {
      state.isCalculateTaxInProgress = true;
      state.isCalculateTaxSuccess = false;
      state.calculatedTaxErrorMsg = '';
    },
    calculateTaxSucceeded: (state, action) => {
      const data = action.payload;
      state.isCalculateTaxInProgress = false;
      state.isCalculateTaxSuccess = true;
      state.calculatedTaxData = data;
      state.calculatedTaxErrorMsg = '';
    },
    calculateTaxFailure: (state, action) => {
      state.isCalculateTaxInProgress = false;
      state.isCalculateTaxSuccess = false;
      state.calculatedTaxData = { details: [] };
      state.calculatedTaxErrorMsg = action.payload;
    },
    resetCalculateTax: (state) => {
      state.isCalculateTaxInProgress = false;
      state.isCalculateTaxSuccess = false;
      state.calculatedTaxErrorMsg = '';
    },
    resetTaxState: () => INITIAL_STATE,
    setPreSelectedTaxPeriod: (state, action) => {
      state.preSelectedPeriod = action.payload;
    },
    clearPreSelectedTaxPeriod: (state) => {
      state.preSelectedPeriod = undefined;
    },
    updateTaxFlowData: (state, action) => {
      state.taxFlowData = {
        ...state.taxFlowData,
        ...action.payload,
      };
    },
  },
});

// Export actions that were generated by createSlice
export const {
  loadLicensesForPeriodStarted,
  loadLicensesForPeriodFailure,
  addLicensesForPeriod,
  taxPaymentPeriodsStarted,
  taxPaymentPeriodSucceeded,
  taxPaymentPeriodFailure,
  calculateTaxStarted,
  calculateTaxSucceeded,
  calculateTaxFailure,
  resetCalculateTax,
  resetTaxState,
  setPreSelectedTaxPeriod,
  clearPreSelectedTaxPeriod,
  updateTaxFlowData,
} = taxSlice.actions;

// Export combined reducer that was generated by createSlice
// (this is a single reducer which is a combination of all reducers in the slice... it basically puts them in a switch statement)
export default taxSlice.reducer;
